import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import ApiDatabase from "server";
import UseContext from "hooks/useContext";
import Modalheader from "components/modal/components/header";
import ModalBody from "components/modal/components/body";
import Banner from "components/banner/banner";
import Button from "components/button";
import PerimeterAccessAlert from "components/modal/dataModal/data/perimeter/PerimeterAccessAlert";
import PerimeterModalFooter from "components/modal/dataModal/data/perimeter/PerimeterModalFooter";
import usePerimeterModal from "components/modal/dataModal/data/perimeter/usePerimeterModal";
import useModal from "components/modal/useModal";
import PerimeterSelect from "components/perimeterMissionStep/PerimeterSelect";
import {
    NO_PERIMETER_CHOICE,
    toPerimeterIdPayload,
} from "components/perimeterMissionStep/perimeterChoice";
import { fillText, getCountText } from "pages/profil/perimeters/perimeterText";

const LABEL_ID = "change-mission-perimeter-label";

const ERROR_KEYS = {
    MISSION_FINISHED: "perimeters.changeMission.errors.missionFinished",
    PERIMETER_ARCHIVED: "perimeters.changeMission.errors.archived",
    PERIMETER_NOT_FOUND: "perimeters.changeMission.errors.notFound",
    INVALID_PERIMETER_ID: "perimeters.changeMission.errors.notFound",
    PERIMETER_FORBIDDEN: "perimeters.changeMission.errors.forbidden",
    MISSION_ACCESS_DENIED: "perimeters.changeMission.errors.missionAccessDenied",
};

const getErrorKey = (err) => ERROR_KEYS[err?.response?.data?.errorCode];

const joinWithAnd = (items, and) =>
    items.length > 1
        ? `${items.slice(0, -1).join(", ")}${and}${items[items.length - 1]}`
        : items[0] || "";

const formatMemberName = ({ firstname, lastname }) =>
    [firstname, lastname?.toUpperCase()].filter(Boolean).join(" ");

// "A et B (P1), C (P2)": members who lose access through the same perimeters
// share one parenthesis, as in the 4.5 mockup.
const formatLosingAccess = (members, and) => {
    const groups = new Map();
    members.forEach((member) => {
        const perimeterNames = (member.perimeterNames || []).join(", ");
        if (!groups.has(perimeterNames)) groups.set(perimeterNames, []);
        groups.get(perimeterNames).push(formatMemberName(member));
    });
    return joinWithAnd(
        [...groups].map(([perimeterNames, names]) => {
            const people = joinWithAnd(names, and);
            return perimeterNames ? `${people} (${perimeterNames})` : people;
        }),
        and
    );
};

// Moves a mission to another perimeter, or to none, right away. Every choice
// is previewed first so the members who would lose access are named before
// the change is confirmed.
const ChangeMissionPerimeterModal = () => {
    const { language, perimeterModalData, setPerimeterModalData } =
        UseContext();
    const { toggle } = useModal();
    const { isSaving, setIsSaving, close } = usePerimeterModal();
    const [{ mission, onChanged }] = useState(() => perimeterModalData || {});
    const idMission = mission?.id;
    const currentChoice = mission?.perimeter?.id || NO_PERIMETER_CHOICE;

    const [options, setOptions] = useState(null);
    const [hasOptionsFailed, setHasOptionsFailed] = useState(false);
    const [optionsAttempt, setOptionsAttempt] = useState(0);
    const [choice, setChoice] = useState(currentChoice);
    // Preview answers per choice: the members losing access, or an error key.
    const [losingByChoice, setLosingByChoice] = useState({});
    const [previewErrorByChoice, setPreviewErrorByChoice] = useState({});
    const [previewAttempt, setPreviewAttempt] = useState(0);
    const [submitErrorKey, setSubmitErrorKey] = useState(null);

    useEffect(() => {
        let cancelled = false;
        setHasOptionsFailed(false);
        ApiDatabase.getPerimeterOptions(
            {},
            (res) => {
                if (cancelled) return;
                setOptions(res?.data || null);
            },
            (err) => {
                if (cancelled) return;
                console.error(err);
                setHasOptionsFailed(true);
            }
        );
        return () => {
            cancelled = true;
        };
    }, [optionsAttempt]);

    useEffect(() => {
        if (choice === currentChoice) return undefined;
        if (choice in losingByChoice || choice in previewErrorByChoice) {
            return undefined;
        }
        let cancelled = false;
        ApiDatabase.getMissionPerimeterChangePreview(
            {
                idMission,
                to: choice === NO_PERIMETER_CHOICE ? "none" : choice,
            },
            (res) => {
                if (cancelled) return;
                const losingAccess = res?.data?.losingAccess;
                setLosingByChoice((known) => ({
                    ...known,
                    [choice]: Array.isArray(losingAccess) ? losingAccess : [],
                }));
            },
            (err) => {
                if (cancelled) return;
                console.error(err);
                setPreviewErrorByChoice((known) => ({
                    ...known,
                    [choice]: getErrorKey(err) || null,
                }));
            }
        );
        return () => {
            cancelled = true;
        };
    }, [
        choice,
        currentChoice,
        idMission,
        losingByChoice,
        previewErrorByChoice,
        previewAttempt,
    ]);

    const isCurrentChoice = choice === currentChoice;
    const losingAccess = losingByChoice[choice];
    const hasPreviewFailed = !isCurrentChoice && choice in previewErrorByChoice;
    const previewErrorKey = previewErrorByChoice[choice];
    const canConfirm = !isCurrentChoice && Array.isArray(losingAccess);

    const handleChoiceChange = (nextChoice) => {
        setSubmitErrorKey(null);
        setChoice(nextChoice);
    };

    const retryPreview = () => {
        setPreviewErrorByChoice((known) => {
            const { [choice]: _failed, ...others } = known;
            return others;
        });
        setPreviewAttempt((count) => count + 1);
    };

    const handleConfirm = () => {
        if (isSaving || !canConfirm) return;
        setIsSaving(true);
        setSubmitErrorKey(null);
        ApiDatabase.changeMissionPerimeter(
            { idMission, perimeterId: toPerimeterIdPayload(choice) },
            (res) => {
                toast.success(language.getValue("perimeters.toast.updated"));
                onChanged?.(res?.data?.perimeter ?? null);
                setIsSaving(false);
                // close() refuses while saving, and its isSaving is still true here.
                setPerimeterModalData(null);
                toggle();
            },
            (err) => {
                console.error(err);
                setIsSaving(false);
                setSubmitErrorKey(
                    getErrorKey(err) || "perimeters.errors.generic"
                );
            }
        );
    };

    const status = mission?.statusKey
        ? language.getValue(
              `perimeters.changeMission.status.${mission.statusKey}`
          )
        : "";
    const recap = fillText(
        language.getValue(
            mission?.number
                ? "perimeters.changeMission.recap"
                : "perimeters.changeMission.recapWithoutNumber"
        ),
        { title: mission?.title || "", number: mission?.number, status }
    );
    const archivedSuffix = mission?.perimeter?.archived
        ? ` ${language.getValue("perimeters.missionDetail.archived")}`
        : "";
    const currentLabel = mission?.perimeter
        ? `${mission.perimeter.name}${archivedSuffix}`
        : undefined;

    return (
        <>
            <Modalheader hide={close} hideShow={true}>
                {language.getValue("perimeters.changeMission.title")}
            </Modalheader>
            <ModalBody>
                <div className="flex flex-col gap-4">
                    <p className="text-sm text-gray-600">
                        {`${recap} ${language.getValue(
                            "perimeters.changeMission.appliesNow"
                        )}`}
                    </p>
                    {hasOptionsFailed ? (
                        <div className="flex flex-col items-start gap-3">
                            <Banner type="alert" showIcon={false}>
                                {language.getValue(
                                    "perimeters.changeMission.optionsError"
                                )}
                            </Banner>
                            <Button
                                color="SECONDARY"
                                size="SM"
                                onClick={() =>
                                    setOptionsAttempt((count) => count + 1)
                                }
                            >
                                {language.getValue("perimeters.list.retry")}
                            </Button>
                        </div>
                    ) : (
                        <div className="flex flex-col gap-1">
                            <span
                                id={LABEL_ID}
                                className="ft-sm font-medium text-gray-700"
                            >
                                {language.getValue(
                                    "perimeters.changeMission.label"
                                )}
                            </span>
                            <PerimeterSelect
                                options={options}
                                value={choice}
                                onChange={handleChoiceChange}
                                fallbackLabel={
                                    choice === currentChoice
                                        ? currentLabel
                                        : undefined
                                }
                                isDisabled={!options || isSaving}
                                isListInline
                                labelId={LABEL_ID}
                            />
                        </div>
                    )}
                    {!isCurrentChoice && losingAccess?.length > 0 && (
                        <PerimeterAccessAlert
                            message={getCountText(
                                language,
                                "perimeters.changeMission.losingAccess",
                                losingAccess.length,
                                {
                                    names: formatLosingAccess(
                                        losingAccess,
                                        language.getValue(
                                            "perimeters.changeMission.and"
                                        )
                                    ),
                                }
                            )}
                        />
                    )}
                    {hasPreviewFailed && (
                        <div className="flex flex-col items-start gap-3">
                            <Banner type="alert" showIcon={false}>
                                {language.getValue(
                                    previewErrorKey ||
                                        "perimeters.changeMission.previewError"
                                )}
                            </Banner>
                            {!previewErrorKey && (
                                <Button
                                    color="SECONDARY"
                                    size="SM"
                                    onClick={retryPreview}
                                >
                                    {language.getValue("perimeters.list.retry")}
                                </Button>
                            )}
                        </div>
                    )}
                    {submitErrorKey && (
                        <Banner type="alert" showIcon={false}>
                            {language.getValue(submitErrorKey)}
                        </Banner>
                    )}
                </div>
            </ModalBody>
            <PerimeterModalFooter
                confirmLabel={language.getValue(
                    "perimeters.changeMission.confirm"
                )}
                savingLabel={language.getValue(
                    "perimeters.changeMission.changing"
                )}
                isSaving={isSaving}
                canConfirm={canConfirm}
                onCancel={close}
                onConfirm={handleConfirm}
            />
        </>
    );
};

export default ChangeMissionPerimeterModal;
